// Regenerate a project from its already-uploaded documents.
//
// The flow:
//   1. fetchDocumentTextsForProject(projectId)
//      → re-downloads each document blob from Supabase Storage and re-runs
//        the same text extractor used during upload. No schema changes,
//        no cached extracts to keep in sync.
//   2. extractProjectData(texts)
//      → reuses the existing edge function so a regeneration is exactly
//        equivalent to a fresh import.
//   3. computeProjectDelta(current, extracted)
//      → builds a structured diff that the UI can render.
//   4. applyProjectDelta(projectId, current, extracted)
//      → wipe-and-rewrite of milestones/risks/KPIs/status updates so the
//        project state matches what was just extracted. Idempotent within
//        a single transactional pass.

import { supabase } from './supabase';
import {
  getProjectDocuments,
  getDocumentDownloadUrl,
} from './db/documents';
import {
  getProjectMilestones,
  createMilestone,
  deleteMilestone,
} from './db/milestones';
import {
  getProjectRisks,
  createRisk,
  deleteRisk,
} from './db/risks';
import {
  getProjectKpis,
  createKpi,
  deleteKpi,
  recordKpiHistory,
} from './db/kpis';
import {
  getProjectStatusUpdates,
  createStatusUpdate,
  deleteStatusUpdate,
} from './db/status-updates';
import { updateProject } from './db/projects';
import { extractTextFromFile, type ProjectExtractionResult } from './llm';
import type { Tables } from '@/types/database.types';

// ---------------------------------------------------------------------------
// Document re-extraction
// ---------------------------------------------------------------------------

export interface RegenerateProgress {
  stage: 'downloading' | 'extracting' | 'analyzing' | 'done';
  completed: number;
  total: number;
  currentFilename?: string;
}

/**
 * Download each document blob from Storage, wrap it in a File, and run it
 * through the standard text extractor. Returns the same string[] shape
 * that the import flow feeds into extractProjectData.
 */
export async function fetchDocumentTextsForProject(
  projectId: string,
  onProgress?: (p: RegenerateProgress) => void,
): Promise<string[]> {
  const docs = await getProjectDocuments(projectId);
  const total = docs.length;
  if (total === 0) return [];

  const texts: string[] = [];
  for (let i = 0; i < docs.length; i++) {
    const doc = docs[i];
    onProgress?.({
      stage: 'downloading',
      completed: i,
      total,
      currentFilename: doc.name,
    });
    try {
      const signedUrl = await getDocumentDownloadUrl(doc.file_path);
      const response = await fetch(signedUrl);
      const blob = await response.blob();
      // extractTextFromFile dispatches by extension on file.name, so the
      // filename MUST carry the extension. doc.name is not reliable for this
      // — some rows (e.g. the demo fixtures) store the name without an
      // extension, which makes the extractor throw "Unsupported file type".
      // The storage path always preserves the original filename including
      // its extension, so derive the name from there. Mime type is only a
      // hint; the extractor doesn't rely on it.
      const fileName = doc.file_path.split('/').pop() || doc.name;
      const file = new File([blob], fileName, {
        type: doc.mime_type ?? blob.type,
      });
      onProgress?.({
        stage: 'extracting',
        completed: i,
        total,
        currentFilename: doc.name,
      });
      const text = await extractTextFromFile(file);
      texts.push(text);
    } catch (err) {
      // Don't sink the whole regenerate if one document fails to download
      // or parse — the LLM can still produce useful output from the rest.
      console.warn(`Failed to re-extract ${doc.name}:`, err);
    }
  }
  return texts;
}

// ---------------------------------------------------------------------------
// Delta computation
// ---------------------------------------------------------------------------

type MilestoneRow = Tables<'milestones'>;
type RiskRow = Tables<'risks'>;
type KpiRow = Tables<'kpis'>;
type StatusUpdateRow = Tables<'status_updates'>;

export interface ProjectMetaChange {
  field: string;
  was: unknown;
  now: unknown;
}

export interface MilestoneDelta {
  added: ProjectExtractionResult['milestones'];
  removed: MilestoneRow[];
  // changed: each entry pairs the existing row with the extracted version
  changed: Array<{ current: MilestoneRow; extracted: ProjectExtractionResult['milestones'][number]; fieldChanges: ProjectMetaChange[] }>;
}

export interface RiskDelta {
  added: ProjectExtractionResult['risks'];
  removed: RiskRow[];
  changed: Array<{ current: RiskRow; extracted: ProjectExtractionResult['risks'][number]; fieldChanges: ProjectMetaChange[] }>;
}

export interface KpiDelta {
  added: ProjectExtractionResult['kpis'];
  removed: KpiRow[];
  changed: Array<{
    current: KpiRow;
    extracted: ProjectExtractionResult['kpis'][number];
    fieldChanges: ProjectMetaChange[];
    historyChange: { current: number; extracted: number };
  }>;
}

export interface StatusListDelta {
  added: string[];
  removed: StatusUpdateRow[];
}

export interface ProjectDelta {
  meta: ProjectMetaChange[];
  milestones: MilestoneDelta;
  risks: RiskDelta;
  kpis: KpiDelta;
  accomplishments: StatusListDelta;
  priorities: StatusListDelta;
}

function normaliseKey(s: string | null | undefined): string {
  return (s ?? '').trim().toLowerCase();
}

/**
 * Compare each project-level scalar field between current and extracted.
 * Skips null-to-null no-ops and trim-equal strings.
 */
function diffProjectMeta(
  current: Tables<'projects'>,
  extracted: ProjectExtractionResult,
): ProjectMetaChange[] {
  const changes: ProjectMetaChange[] = [];
  const pairs: Array<{ field: string; was: unknown; now: unknown }> = [
    { field: 'Name', was: current.name, now: extracted.projectName },
    { field: 'Tracker', was: current.external_id, now: extracted.trackerId },
    { field: 'Description', was: current.description, now: extracted.description },
    { field: 'Executive summary', was: current.executive_summary, now: extracted.summary },
    { field: 'Health', was: current.overall_health, now: extracted.overallHealth },
    { field: 'Start date', was: current.start_date, now: extracted.startDate },
    { field: 'End date', was: current.end_date, now: extracted.endDate },
    { field: 'Completion %', was: current.completion_percentage, now: extracted.completionPercentage },
  ];
  for (const p of pairs) {
    const wasNorm = typeof p.was === 'string' ? p.was.trim() : p.was;
    const nowNorm = typeof p.now === 'string' ? (p.now as string).trim() : p.now;
    if ((wasNorm ?? null) !== (nowNorm ?? null)) {
      changes.push(p);
    }
  }
  return changes;
}

function diffMilestones(
  current: MilestoneRow[],
  extracted: ProjectExtractionResult['milestones'],
): MilestoneDelta {
  const currentByKey = new Map(current.map((m) => [normaliseKey(m.name), m]));
  const extractedByKey = new Map(extracted.map((m) => [normaliseKey(m.name), m]));

  const added = extracted.filter((m) => !currentByKey.has(normaliseKey(m.name)));
  const removed = current.filter((m) => !extractedByKey.has(normaliseKey(m.name)));

  const changed: MilestoneDelta['changed'] = [];
  for (const c of current) {
    const e = extractedByKey.get(normaliseKey(c.name));
    if (!e) continue;
    const fieldChanges: ProjectMetaChange[] = [];
    if ((c.start_date ?? null) !== (e.startDate ?? null)) {
      fieldChanges.push({ field: 'Start date', was: c.start_date, now: e.startDate });
    }
    if ((c.due_date ?? null) !== (e.dueDate ?? null)) {
      fieldChanges.push({ field: 'Due date', was: c.due_date, now: e.dueDate });
    }
    if ((c.status ?? 'not_started') !== e.status) {
      fieldChanges.push({ field: 'Status', was: c.status, now: e.status });
    }
    if ((c.owner_name ?? null) !== (e.owner ?? null)) {
      fieldChanges.push({ field: 'Owner', was: c.owner_name, now: e.owner });
    }
    if (fieldChanges.length > 0) {
      changed.push({ current: c, extracted: e, fieldChanges });
    }
  }
  return { added, removed, changed };
}

function diffRisks(
  current: RiskRow[],
  extracted: ProjectExtractionResult['risks'],
): RiskDelta {
  const currentByKey = new Map(current.map((r) => [normaliseKey(r.summary), r]));
  const extractedByKey = new Map(extracted.map((r) => [normaliseKey(r.summary), r]));

  const added = extracted.filter((r) => !currentByKey.has(normaliseKey(r.summary)));
  const removed = current.filter((r) => !extractedByKey.has(normaliseKey(r.summary)));

  const changed: RiskDelta['changed'] = [];
  for (const c of current) {
    const e = extractedByKey.get(normaliseKey(c.summary));
    if (!e) continue;
    const fieldChanges: ProjectMetaChange[] = [];
    if (c.severity !== e.severity) {
      fieldChanges.push({ field: 'Severity', was: c.severity, now: e.severity });
    }
    if ((c.status ?? 'open') !== e.status) {
      fieldChanges.push({ field: 'Status', was: c.status, now: e.status });
    }
    if ((c.owner_name ?? null) !== (e.owner ?? null)) {
      fieldChanges.push({ field: 'Owner', was: c.owner_name, now: e.owner });
    }
    if ((c.mitigation_plan ?? null) !== (e.mitigation ?? null)) {
      fieldChanges.push({ field: 'Mitigation', was: c.mitigation_plan, now: e.mitigation });
    }
    if (fieldChanges.length > 0) {
      changed.push({ current: c, extracted: e, fieldChanges });
    }
  }
  return { added, removed, changed };
}

function diffKpis(
  current: KpiRow[],
  currentHistoryByKpi: Map<string, { value: number; recorded_at: string }[]>,
  extracted: ProjectExtractionResult['kpis'],
): KpiDelta {
  const currentByKey = new Map(current.map((k) => [normaliseKey(k.name), k]));
  const extractedByKey = new Map(extracted.map((k) => [normaliseKey(k.name), k]));

  const added = extracted.filter((k) => !currentByKey.has(normaliseKey(k.name)));
  const removed = current.filter((k) => !extractedByKey.has(normaliseKey(k.name)));

  const changed: KpiDelta['changed'] = [];
  for (const c of current) {
    const e = extractedByKey.get(normaliseKey(c.name));
    if (!e) continue;
    const fieldChanges: ProjectMetaChange[] = [];
    if ((c.target ?? null) !== (e.target ?? null)) {
      fieldChanges.push({ field: 'Target', was: c.target, now: e.target });
    }
    if ((c.current_value ?? null) !== (e.current ?? null)) {
      fieldChanges.push({ field: 'Current value', was: c.current_value, now: e.current });
    }
    if ((c.unit ?? null) !== (e.unit ?? null)) {
      fieldChanges.push({ field: 'Unit', was: c.unit, now: e.unit });
    }
    const currentHistoryCount = currentHistoryByKpi.get(c.id)?.length ?? 0;
    const extractedHistoryCount = e.history.length;
    const historyChange = { current: currentHistoryCount, extracted: extractedHistoryCount };
    if (fieldChanges.length > 0 || currentHistoryCount !== extractedHistoryCount) {
      changed.push({ current: c, extracted: e, fieldChanges, historyChange });
    }
  }
  return { added, removed, changed };
}

function diffStatusList(
  current: StatusUpdateRow[],
  extracted: string[],
): StatusListDelta {
  const currentSet = new Set(current.map((s) => normaliseKey(s.content)));
  const extractedSet = new Set(extracted.map((s) => normaliseKey(s)));
  return {
    added: extracted.filter((s) => !currentSet.has(normaliseKey(s))),
    removed: current.filter((s) => !extractedSet.has(normaliseKey(s.content))),
  };
}

export interface CurrentProjectSnapshot {
  project: Tables<'projects'>;
  milestones: MilestoneRow[];
  risks: RiskRow[];
  kpis: KpiRow[];
  kpiHistory: Map<string, { value: number; recorded_at: string }[]>;
  accomplishments: StatusUpdateRow[];
  priorities: StatusUpdateRow[];
}

export function computeProjectDelta(
  current: CurrentProjectSnapshot,
  extracted: ProjectExtractionResult,
): ProjectDelta {
  return {
    meta: diffProjectMeta(current.project, extracted),
    milestones: diffMilestones(current.milestones, extracted.milestones),
    risks: diffRisks(current.risks, extracted.risks),
    kpis: diffKpis(current.kpis, current.kpiHistory, extracted.kpis),
    accomplishments: diffStatusList(current.accomplishments, extracted.accomplishments),
    priorities: diffStatusList(current.priorities, extracted.priorities),
  };
}

// ---------------------------------------------------------------------------
// Apply
// ---------------------------------------------------------------------------

/** Replace the project state to match the extracted result. */
export async function applyProjectDelta(
  projectId: string,
  current: CurrentProjectSnapshot,
  extracted: ProjectExtractionResult,
): Promise<void> {
  // 1) Project-level fields
  await updateProject(projectId, {
    name: extracted.projectName.trim() || current.project.name,
    description: extracted.description.trim() || null,
    executive_summary: extracted.summary.trim() || null,
    overall_health: extracted.overallHealth,
    start_date: extracted.startDate,
    end_date: extracted.endDate,
    completion_percentage: extracted.completionPercentage,
    external_id: extracted.trackerId || null,
  });

  // 2) Wipe-and-rewrite milestones / risks / KPIs / accomplishments / priorities.
  //    Sequential to keep error handling sane — each await failure surfaces
  //    immediately to the caller.
  await Promise.all([
    ...current.milestones.map((m) => deleteMilestone(m.id)),
    ...current.risks.map((r) => deleteRisk(r.id)),
    ...current.kpis.map((k) => deleteKpi(k.id)),
    ...current.accomplishments.map((s) => deleteStatusUpdate(s.id)),
    ...current.priorities.map((s) => deleteStatusUpdate(s.id)),
  ]);

  for (const m of extracted.milestones) {
    if (!m.name.trim()) continue;
    await createMilestone(projectId, {
      name: m.name,
      startDate: m.startDate,
      dueDate: m.dueDate,
      status: m.status,
      ownerName: m.owner,
    });
  }
  for (const r of extracted.risks) {
    if (!r.summary.trim()) continue;
    await createRisk(projectId, {
      summary: r.summary,
      description: r.description || undefined,
      severity: r.severity,
      status: r.status,
      identifiedDate: r.dateIdentified,
      ownerName: r.owner,
      mitigationPlan: r.mitigation,
    });
  }
  for (const k of extracted.kpis) {
    if (!k.name.trim()) continue;
    const created = await createKpi(projectId, {
      name: k.name,
      target: k.target,
      currentValue: k.current,
      unit: k.unit,
    });
    if (k.history && k.history.length > 0) {
      await Promise.all(
        k.history.map((point) =>
          recordKpiHistory(created.id, point.value, `${point.date}T12:00:00.000Z`).catch((err) =>
            console.warn(`Failed to backfill kpi_history for ${k.name}:`, err),
          ),
        ),
      );
    }
  }
  for (const a of extracted.accomplishments) {
    if (!a.trim()) continue;
    await createStatusUpdate(projectId, { type: 'accomplishment', content: a });
  }
  for (const p of extracted.priorities) {
    if (!p.trim()) continue;
    await createStatusUpdate(projectId, { type: 'priority', content: p });
  }
}

// ---------------------------------------------------------------------------
// Snapshot the current project state in one call (used by the dialog).
// ---------------------------------------------------------------------------

export async function loadCurrentProjectSnapshot(
  projectId: string,
): Promise<CurrentProjectSnapshot | null> {
  const { data: project, error } = await supabase
    .from('projects')
    .select('*')
    .eq('id', projectId)
    .single();
  if (error || !project) return null;

  const [milestones, risks, kpis, statusUpdates] = await Promise.all([
    getProjectMilestones(projectId),
    getProjectRisks(projectId),
    getProjectKpis(projectId),
    getProjectStatusUpdates(projectId),
  ]);

  // Pull KPI history once and reuse for the diff. The dashboard's helper
  // already returns id + value + recorded_at; we only need the count for
  // the diff so we strip id here.
  const { getProjectKpiHistory } = await import('./db/kpis');
  const historyMap = await getProjectKpiHistory(projectId);
  const kpiHistory = new Map<string, { value: number; recorded_at: string }[]>();
  for (const [kpiId, points] of historyMap.entries()) {
    kpiHistory.set(
      kpiId,
      points.map((p) => ({ value: p.value, recorded_at: p.recorded_at })),
    );
  }

  return {
    project,
    milestones,
    risks,
    kpis,
    kpiHistory,
    accomplishments: statusUpdates.filter((s) => s.type === 'accomplishment'),
    priorities: statusUpdates.filter((s) => s.type === 'priority'),
  };
}

export function deltaIsEmpty(delta: ProjectDelta): boolean {
  return (
    delta.meta.length === 0 &&
    delta.milestones.added.length === 0 &&
    delta.milestones.removed.length === 0 &&
    delta.milestones.changed.length === 0 &&
    delta.risks.added.length === 0 &&
    delta.risks.removed.length === 0 &&
    delta.risks.changed.length === 0 &&
    delta.kpis.added.length === 0 &&
    delta.kpis.removed.length === 0 &&
    delta.kpis.changed.length === 0 &&
    delta.accomplishments.added.length === 0 &&
    delta.accomplishments.removed.length === 0 &&
    delta.priorities.added.length === 0 &&
    delta.priorities.removed.length === 0
  );
}
